import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { Eye, Download, Printer, MessageCircle, RefreshCw, Ban, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { certError, downloadCert, printCert } from "@/lib/certs";
import { CertViewDialog } from "@/components/certs/CertViewDialog";
import { CertShareDialog } from "@/components/certs/CertShareDialog";

const ReasonDialog = ({ mode, cert, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const revoke = mode === "revoke";
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/certificates/${cert.id}/${mode}`, { reason });
      toast.success(revoke ? "Certificate revoked" : `Reissued as ${data.certificate.certificate_number}`);
      if (data?.error) toast.error(data.error);
      setReason(""); onDone();
    } catch (e) { toast.error(await certError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!mode} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid="cert-reason-dialog">
        <DialogHeader><DialogTitle className="font-head">{revoke ? "Revoke" : "Reissue"} {cert?.certificate_number}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{revoke
          ? "The certificate will show as Revoked on its verification page. This can't be undone; you can issue a new certificate later."
          : "A new certificate number is issued with the current logo and design. The old one is marked Reissued and kept in history."}</p>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="cert-reason-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} variant={revoke ? "destructive" : "default"} data-testid="cert-reason-confirm">{revoke ? "Revoke" : "Reissue"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CertificateActions = ({ cert, onChanged, size = "sm", labels = true }) => {
  const { can } = useApp();
  const [view, setView] = useState(null);
  const [share, setShare] = useState(null);
  const [mode, setMode] = useState(null);
  const closeView = useCallback(() => setView(null), []);
  const closeShare = useCallback(() => setShare(null), []);
  if (!cert) return null;
  const run = (fn) => async () => { try { await fn(cert); } catch (e) { toast.error(await certError(e)); } };
  const retry = async () => {
    try { await api.post(`/certificates/${cert.id}/regenerate-pdf`); toast.success("PDF generated"); onChanged?.(); }
    catch (e) { toast.error(await certError(e)); }
  };
  const tid = (a) => `cert-${a}-${cert.certificate_number}`;
  const B = ({ icon: I, label, onClick, t, cls = "" }) => (
    <Button size={size} variant="outline" onClick={onClick} className={`active:scale-95 ${cls}`} title={label} data-testid={tid(t)}>
      <I className={`h-3.5 w-3.5 ${labels ? "mr-1.5" : ""}`} />{labels && label}
    </Button>
  );
  const live = cert.status === "Valid";
  return (
    <div className="flex flex-wrap gap-1.5">
      {cert.pdf_status === "failed" && can("cert.issue") && <B icon={RotateCw} label="Retry PDF" onClick={retry} t="retry" cls="border-amber-500/50 text-amber-500" />}
      {can("cert.download") && <B icon={Eye} label="View" onClick={() => setView(cert)} t="view" />}
      {can("cert.download") && <B icon={Download} label="Download PDF" onClick={run(downloadCert)} t="download" />}
      {can("cert.download") && <B icon={Printer} label="Print" onClick={run(printCert)} t="print" />}
      {can("cert.share") && live && <B icon={MessageCircle} label="Share on WhatsApp" onClick={() => setShare(cert)} t="share" cls="text-emerald-500" />}
      {can("cert.issue") && cert.status === "Valid" && <B icon={RefreshCw} label="Reissue" onClick={() => setMode("reissue")} t="reissue" />}
      {can("cert.revoke") && live && <B icon={Ban} label="Revoke" onClick={() => setMode("revoke")} t="revoke" cls="text-rose-500" />}
      <CertViewDialog cert={view} onClose={closeView} />
      <CertShareDialog cert={share} onClose={closeShare} />
      <ReasonDialog mode={mode} cert={cert} onClose={() => setMode(null)} onDone={() => { setMode(null); onChanged?.(); }} />
    </div>
  );
};
